import { apiRequest } from '@/lib/api/client'
import { sanitizeCampaignSocialLinksInput } from '@/lib/campaign-social-links'

export type CampaignStatus = 'draft' | 'active' | 'finished' | 'cancelled'
export type RegulationContentType = 'document' | 'text'

export type PublicCampaignTemplateOption = {
  key: string
  name: string
  description: string
}

export type CampaignCarouselExistingToken = `existing:${number}`
export type CampaignCarouselNewToken = `new:${string}`
export type CampaignCarouselOrderToken =
  | CampaignCarouselExistingToken
  | CampaignCarouselNewToken
export type CampaignCarouselUploadMap = Record<string, File>

export type CampaignImage = {
  id: number
  campaign_id: number
  image_url: string
  position: number
}

export type CampaignPremiatedLuckyNumberEntry = {
  number: string
  prize_description: string
}

export type CampaignPremiatedNumberAssignmentDonor = {
  id: number
  person_type: string | null
  full_name: string | null
  name: string | null
  corporate_name: string | null
  email: string | null
  phone: string | null
  document_cpf: string | null
  document_cnpj: string | null
}

export type CampaignPremiatedNumberAssignment = {
  number: string
  prize_description: string | null
  payment_id: number | null
  assigned_at: string | null
  donor: CampaignPremiatedNumberAssignmentDonor | null
}

export type Campaign = {
  id: number
  name: string
  slug?: string | null
  title?: string | null
  description?: string | null
  prize_description?: string | null
  starts_at?: string | null
  ends_at?: string | null
  generation_status?: 'pending' | 'generating' | 'completed' | 'failed' | 'donation_only'
  total_numbers?: number | null
  min_quantity_per_purchase?: number | null
  max_quantity_per_purchase?: number | null
  price?: number | string | null
  doey_seller_id?: string | null
  cta_text?: string | null
  status?: CampaignStatus | null
  /** Quando true, a campanha some dos seletores principais e da listagem principal do painel. */
  hidden_from_main?: boolean | null
  whatsapp_button_url?: string | null
  public_page_url?: string | null
  custom_domain?: string | null
  primary_color?: string | null
  secondary_color?: string | null
  button_color?: string | null
  widget_embed_button_text?: string | null
  widget_embed_button_color?: string | null
  widget_embed_button_text_color?: string | null
  widget_embed_button_radius_px?: number | null
  pix_qr_expiration_minutes?: number | null
  highlight_color?: string | null
  main_image_url?: string | null
  youtube_video_url?: string | null
  regulation_pdf_url?: string | null
  regulation_content_type?: RegulationContentType | null
  regulation_text?: string | null
  certificate_pdf_url?: string | null
  login_url?: string | null
  public_reviews_text?: string | null
  public_live_badge_text?: string | null
  public_about_url?: string | null
  public_contact_url?: string | null
  public_social_links?: Record<string, string> | null
  public_terms_url?: string | null
  public_privacy_url?: string | null
  public_notice_text?: string | null
  public_awarded_titles_html?: string | null
  public_details_html?: string | null
  public_page_title?: string | null
  public_share_title?: string | null
  public_share_description?: string | null
  public_share_image_url?: string | null
  public_template_key?: string | null
  template3_eyebrow?: string | null
  template3_hero_background_url?: string | null
  template3_about_title?: string | null
  template3_about_text?: string | null
  template3_about_image_url?: string | null
  template3_floating_cta_image_url?: string | null
  template3_advantages?: Array<{
    title?: string | null
    description?: string | null
    image_url?: string | null
  }> | null
  template3_faq?: Array<{
    question?: string | null
    answer?: string | null
  }> | null
  fb_pixel?: string | null
  fb_access_token?: string | null
  g_pixel?: string | null
  gtm_measurement_id?: string | null
  ga4_measurement_protocol_secret?: string | null
  certificate_number?: string | null
  email_template_subject?: string | null
  email_template_body?: string | null
  premiated_lucky_numbers?: CampaignPremiatedLuckyNumberEntry[] | null
  /** Somente resposta autenticada da instituição ou admin na campanha */
  premiated_number_assignments?: CampaignPremiatedNumberAssignment[] | null
  campaign_images?: CampaignImage[] | null
  created_at?: string | null
  updated_at?: string | null
}

type CampaignListResponse = {
  campaigns?: Campaign[]
}

type CampaignResponse = {
  campaign?: Campaign
}

type PublicCampaignTemplatesResponse = {
  templates?: PublicCampaignTemplateOption[]
}

export type CampaignUpsertInput = {
  /** Define campanha como compra sem números da sorte (imutável; sem geração de números). */
  donation_only?: boolean
  name?: string
  slug?: string
  title?: string
  description?: string
  prize_description?: string
  starts_at?: string
  ends_at?: string
  total_numbers?: string
  price?: string
  min_quantity_per_purchase?: string
  max_quantity_per_purchase?: string
  doey_seller_id?: string
  seller_id_totp_code?: string
  cta_text?: string
  status?: CampaignStatus
  whatsapp_button_url?: string
  primary_color?: string
  secondary_color?: string
  button_color?: string
  widget_embed_button_text?: string
  widget_embed_button_color?: string
  widget_embed_button_text_color?: string
  widget_embed_button_radius_px?: string
  pix_qr_expiration_minutes?: string
  highlight_color?: string
  youtube_video_url?: string
  main_image?: File | null
  sync_carousel?: boolean
  carousel_order?: CampaignCarouselOrderToken[]
  carousel_images?: CampaignCarouselUploadMap
  regulation_pdf?: File | null
  regulation_content_type?: RegulationContentType
  regulation_text?: string
  certificate_pdf?: File | null
  login_url?: string
  public_page_url?: string
  custom_domain?: string
  public_reviews_text?: string
  public_live_badge_text?: string
  public_about_url?: string
  public_contact_url?: string
  public_social_links?: Record<string, string>
  public_notice_text?: string
  public_awarded_titles_html?: string
  public_details_html?: string
  public_page_title?: string
  public_share_title?: string
  public_share_description?: string
  public_template_key?: string
  template3_eyebrow?: string
  template3_about_title?: string
  template3_about_text?: string
  template3_advantages?: Array<{
    title: string
    description: string
    image_url?: string | null
  }>
  template3_faq?: Array<{
    question: string
    answer: string
  }>
  template3_hero_background?: File | null
  template3_about_image?: File | null
  template3_advantage_image_1?: File | null
  template3_advantage_image_2?: File | null
  template3_advantage_image_3?: File | null
  template3_hero_background_url?: string | null
  template3_about_image_url?: string | null
  template3_floating_cta_image?: File | null
  template3_floating_cta_image_url?: string | null
  public_share_image?: File | null
  fb_pixel?: string
  fb_access_token?: string
  g_pixel?: string
  gtm_measurement_id?: string
  ga4_measurement_protocol_secret?: string
  certificate_number?: string
  email_template_subject?: string
  email_template_body?: string
  premiated_lucky_numbers?: CampaignPremiatedLuckyNumberEntry[]
  /** Somente leitura vinda da API após carregar ou salvar a campanha autenticado */
  premiated_number_assignments?: CampaignPremiatedNumberAssignment[]
  hidden_from_main?: boolean
}

/** Controle de envio de segredos de marketing no FormData (omitir = não alterar no backend). */
export type CampaignMarketingSecretsOptions = {
  includeFbAccessToken?: boolean
  includeGa4MeasurementProtocolSecret?: boolean
}

export type CampaignFormDataOptions = {
  marketingSecrets?: CampaignMarketingSecretsOptions
}

function appendIf(formData: FormData, key: string, value: string | File | null | undefined) {
  if (value === undefined || value === null || value === '') return
  formData.append(key, value)
}

function appendNullableString(formData: FormData, key: string, value: string | null | undefined) {
  if (value === undefined) return
  formData.append(key, value ?? '')
}

/** Normaliza o JSON de números premiados retornado pela API (formato atual ou legado em lista de strings). */
export function parsePremiatedLuckyNumbersFromApi(raw: unknown): CampaignPremiatedLuckyNumberEntry[] {
  if (!Array.isArray(raw)) {
    return []
  }
  const out: CampaignPremiatedLuckyNumberEntry[] = []
  for (const item of raw) {
    if (typeof item === 'string') {
      const number = item.replace(/\D/g, '')
      if (number !== '') {
        out.push({ number, prize_description: '' })
      }
      continue
    }
    if (item && typeof item === 'object' && 'number' in item) {
      const record = item as Record<string, unknown>
      const number = typeof record.number === 'string' ? record.number.replace(/\D/g, '') : ''
      const prize_description =
        typeof record.prize_description === 'string' ? record.prize_description : ''
      if (number !== '' || prize_description.trim() !== '') {
        out.push({ number, prize_description })
      }
    }
  }
  return out
}

export function buildCampaignFormData(
  input: CampaignUpsertInput,
  options: CampaignFormDataOptions = {}
): FormData {
  const data = new FormData()
  const marketingSecrets = options.marketingSecrets ?? {}
  const includeFbAccessToken = marketingSecrets.includeFbAccessToken ?? true
  const includeGa4Secret = marketingSecrets.includeGa4MeasurementProtocolSecret ?? true

  appendIf(data, 'name', input.name)
  appendIf(data, 'slug', input.slug)
  appendIf(data, 'title', input.title)
  appendIf(data, 'description', input.description)
  appendIf(data, 'prize_description', input.prize_description)
  appendIf(data, 'starts_at', input.starts_at)
  appendIf(data, 'ends_at', input.ends_at)
  if (input.donation_only === true) {
    data.append('donation_only', '1')
  }

  // Backend espera total_numbers como inteiro; enviar apenas valor numérico válido >= 1
  if (input.total_numbers !== undefined && input.total_numbers !== null && input.total_numbers !== '') {
    const num = parseInt(String(input.total_numbers).replace(/\s/g, ''), 10)
    if (!Number.isNaN(num) && num >= 1) {
      data.append('total_numbers', String(num))
    }
  }
  appendIf(data, 'price', input.price)
  appendIf(data, 'min_quantity_per_purchase', input.min_quantity_per_purchase)
  appendIf(data, 'max_quantity_per_purchase', input.max_quantity_per_purchase)
  appendIf(data, 'doey_seller_id', input.doey_seller_id)
  appendIf(data, 'seller_id_totp_code', input.seller_id_totp_code)
  appendIf(data, 'cta_text', input.cta_text)
  appendIf(data, 'status', input.status)
  appendIf(data, 'whatsapp_button_url', input.whatsapp_button_url)
  appendIf(data, 'primary_color', input.primary_color)
  appendIf(data, 'secondary_color', input.secondary_color)
  appendIf(data, 'button_color', input.button_color)
  appendNullableString(data, 'widget_embed_button_text', input.widget_embed_button_text)
  appendNullableString(data, 'widget_embed_button_color', input.widget_embed_button_color)
  appendNullableString(data, 'widget_embed_button_text_color', input.widget_embed_button_text_color)
  appendNullableString(data, 'widget_embed_button_radius_px', input.widget_embed_button_radius_px)
  appendNullableString(data, 'pix_qr_expiration_minutes', input.pix_qr_expiration_minutes)
  appendIf(data, 'highlight_color', input.highlight_color)
  appendIf(data, 'youtube_video_url', input.youtube_video_url)
  appendNullableString(data, 'regulation_content_type', input.regulation_content_type)

  if (input.main_image) {
    data.append('main_image', input.main_image)
  }

  if (input.sync_carousel !== undefined) {
    data.append('sync_carousel', input.sync_carousel ? '1' : '0')
  }

  if (input.carousel_order && input.carousel_order.length > 0) {
    input.carousel_order.forEach((token) => {
      data.append('carousel_order[]', token)
    })
  }

  if (input.carousel_images) {
    Object.entries(input.carousel_images).forEach(([key, file]) => {
      data.append(`carousel_images[${key}]`, file)
    })
  }

  if (input.regulation_pdf) {
    data.append('regulation_pdf', input.regulation_pdf)
  }

  appendNullableString(data, 'regulation_text', input.regulation_text)

  if (input.certificate_pdf) {
    data.append('certificate_pdf', input.certificate_pdf)
  }

  appendIf(data, 'login_url', input.login_url)
  appendNullableString(data, 'public_page_url', input.public_page_url)
  appendNullableString(data, 'custom_domain', input.custom_domain)
  appendIf(data, 'public_reviews_text', input.public_reviews_text)
  appendIf(data, 'public_live_badge_text', input.public_live_badge_text)
  appendIf(data, 'public_about_url', input.public_about_url)
  appendIf(data, 'public_contact_url', input.public_contact_url)
  if (input.public_social_links !== undefined) {
    data.append(
      'public_social_links',
      JSON.stringify(sanitizeCampaignSocialLinksInput(input.public_social_links))
    )
  }
  appendIf(data, 'public_notice_text', input.public_notice_text)
  appendIf(data, 'public_awarded_titles_html', input.public_awarded_titles_html)
  appendIf(data, 'public_details_html', input.public_details_html)
  appendNullableString(data, 'public_page_title', input.public_page_title)
  appendNullableString(data, 'public_share_title', input.public_share_title)
  appendNullableString(data, 'public_share_description', input.public_share_description)
  appendIf(data, 'public_template_key', input.public_template_key)
  if (input.public_template_key === 'template-3') {
    appendIf(data, 'template3_eyebrow', input.template3_eyebrow)
    appendIf(data, 'template3_about_title', input.template3_about_title)
    appendIf(data, 'template3_about_text', input.template3_about_text)
    if (input.template3_advantages !== undefined) {
      data.append(
        'template3_advantages',
        JSON.stringify(
          input.template3_advantages.map((item) => ({
            title: item.title,
            description: item.description,
          }))
        )
      )
    }
    if (input.template3_faq !== undefined) {
      data.append('template3_faq', JSON.stringify(input.template3_faq))
    }
    if (input.template3_hero_background) {
      data.append('template3_hero_background', input.template3_hero_background)
    }
    if (input.template3_about_image) {
      data.append('template3_about_image', input.template3_about_image)
    }
    if (input.template3_floating_cta_image) {
      data.append('template3_floating_cta_image', input.template3_floating_cta_image)
    }
    if (input.template3_advantage_image_1) {
      data.append('template3_advantage_image_1', input.template3_advantage_image_1)
    }
    if (input.template3_advantage_image_2) {
      data.append('template3_advantage_image_2', input.template3_advantage_image_2)
    }
    if (input.template3_advantage_image_3) {
      data.append('template3_advantage_image_3', input.template3_advantage_image_3)
    }
  }
  if (input.public_share_image) {
    data.append('public_share_image', input.public_share_image)
  }
  appendNullableString(data, 'fb_pixel', input.fb_pixel)
  if (includeFbAccessToken) {
    appendNullableString(data, 'fb_access_token', input.fb_access_token)
  }
  appendNullableString(data, 'g_pixel', input.g_pixel)
  appendNullableString(data, 'gtm_measurement_id', input.gtm_measurement_id)
  if (includeGa4Secret) {
    appendNullableString(data, 'ga4_measurement_protocol_secret', input.ga4_measurement_protocol_secret)
  }
  appendIf(data, 'certificate_number', input.certificate_number)
  if (input.premiated_lucky_numbers !== undefined) {
    const cleaned = input.premiated_lucky_numbers
      .map((entry) => ({
        number: String(entry.number).trim().replace(/\D/g, ''),
        prize_description: String(entry.prize_description ?? '').trim(),
      }))
      .filter((entry) => entry.number.length > 0)
    data.append('premiated_lucky_numbers', JSON.stringify(cleaned))
  }
  if (input.hidden_from_main !== undefined) {
    data.append('hidden_from_main', input.hidden_from_main ? '1' : '0')
  }

  return data
}

export async function fetchCampaigns(token?: string): Promise<Campaign[]> {
  const response = await apiRequest<CampaignListResponse>('/api/campaigns', {
    token,
  })
  return response.campaigns ?? []
}

export async function fetchPublicCampaignTemplates(token?: string): Promise<PublicCampaignTemplateOption[]> {
  const response = await apiRequest<PublicCampaignTemplatesResponse>('/api/campaigns/public-templates', {
    token,
  })

  return response.templates ?? []
}

export async function fetchCampaignById(id: number, token?: string): Promise<Campaign> {
  const endpoint = token ? `/api/campaigns/${id}/editor` : `/api/campaigns/${id}`
  const response = await apiRequest<CampaignResponse>(endpoint, { token })
  if (!response.campaign) {
    throw new Error('Campanha não encontrada.')
  }
  return response.campaign
}

export async function createCampaign(
  token: string,
  input: CampaignUpsertInput,
  options?: CampaignFormDataOptions
): Promise<Campaign> {
  const response = await apiRequest<CampaignResponse>('/api/campaigns', {
    method: 'POST',
    body: buildCampaignFormData(input, options),
    token,
  })
  if (!response.campaign) {
    throw new Error('Não foi possível criar a campanha.')
  }
  return response.campaign
}

export async function updateCampaign(
  token: string,
  id: number,
  input: CampaignUpsertInput,
  options?: CampaignFormDataOptions
): Promise<Campaign> {
  const formData = buildCampaignFormData(input, options)
  formData.append('_method', 'PUT')
  const response = await apiRequest<CampaignResponse>(`/api/campaigns/${id}`, {
    method: 'POST',
    body: formData,
    token,
  })
  if (!response.campaign) {
    throw new Error('Não foi possível atualizar a campanha.')
  }
  return response.campaign
}

export async function duplicateCampaign(token: string, id: number): Promise<Campaign> {
  const response = await apiRequest<CampaignResponse>(`/api/campaigns/${id}/duplicate`, {
    method: 'POST',
    token,
  })
  if (!response.campaign) throw new Error('Não foi possível duplicar a campanha.')
  return response.campaign
}
